import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  fetchAssigneeSuggestions,
  fetchMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AssigneeSuggestion,
  type TaskNotification,
} from '@/lib/api';
import { useAuth } from './AuthContext';
import { normalizeAssigneeKey } from '@/lib/assigneeDisplay';

interface NotificationsContextValue {
  notifications: TaskNotification[];
  unreadCount: number;
  loading: boolean;
  /** Принудительно обновить из сервера (после сохранения задач). */
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Карта "нормализованный ключ → совпавший пользователь" для бейджей на Ганте. */
  assigneeMatches: Map<string, AssigneeSuggestion>;
  /** Запросить новые саджесты (результаты склеиваются в кэш assigneeMatches). */
  fetchSuggestions: (query: string) => Promise<AssigneeSuggestion[]>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [assigneeMatches, setAssigneeMatches] = useState<Map<string, AssigneeSuggestion>>(
    new Map(),
  );
  const inflight = useRef<Promise<void> | null>(null);

  const mergeMatches = useCallback((items: AssigneeSuggestion[]) => {
    if (items.length === 0) return;
    setAssigneeMatches((prev) => {
      const next = new Map(prev);
      for (const it of items) {
        next.set(normalizeAssigneeKey(it.login), it);
        if (it.displayName && it.displayName.trim()) {
          next.set(normalizeAssigneeKey(it.displayName), it);
        }
      }
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    if (inflight.current) return inflight.current;
    setLoading(true);
    const p = (async () => {
      try {
        const data = await fetchMyNotifications({ includeResolved: true });
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      } catch (e) {
        console.warn('fetch notifications failed', e);
      } finally {
        setLoading(false);
        inflight.current = null;
      }
    })();
    inflight.current = p;
    return p;
  }, [user]);

  // Начальная загрузка + прелоад саджестов (для матчинга на Ганте).
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setAssigneeMatches(new Map());
      return;
    }
    refresh();
    // Прелоад полного списка пользователей для матчинга на Ганте.
    fetchAssigneeSuggestions('', 500)
      .then((items) => mergeMatches(items))
      .catch(() => {});
  }, [user, refresh, mergeMatches]);

  // Периодический опрос раз в 60 сек: просроченные/вновь назначенные появляются без перезагрузки.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      refresh();
    }, 60_000);
    return () => clearInterval(t);
  }, [user, refresh]);

  // Немедленный обновление после сохранения проектов (фиксация смены исполнителя/статуса).
  useEffect(() => {
    if (!user) return;
    const h = () => refresh();
    window.addEventListener('diak:projects-saved', h);
    return () => window.removeEventListener('diak:projects-saved', h);
  }, [user, refresh]);

  const markRead = useCallback(
    async (id: string) => {
      setNotifications((list) =>
        list.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await markNotificationRead(id);
      } catch (e) {
        console.warn('markRead failed', e);
        refresh();
      }
    },
    [refresh],
  );

  const markAllRead = useCallback(async () => {
    setNotifications((list) => list.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch (e) {
      console.warn('markAllRead failed', e);
      refresh();
    }
  }, [refresh]);

  const fetchSuggestions = useCallback(
    async (query: string): Promise<AssigneeSuggestion[]> => {
      try {
        const items = await fetchAssigneeSuggestions(query);
        mergeMatches(items);
        return items;
      } catch {
        return [];
      }
    },
    [mergeMatches],
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      loading,
      refresh,
      markRead,
      markAllRead,
      assigneeMatches,
      fetchSuggestions,
    }),
    [
      notifications,
      unreadCount,
      loading,
      refresh,
      markRead,
      markAllRead,
      assigneeMatches,
      fetchSuggestions,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}

/** Попытаться сопоставить строку-исполнителя с зарегистрированным/приглашённым пользователем. */
export function useAssigneeMatch(raw: string): AssigneeSuggestion | null {
  const { assigneeMatches } = useNotifications();
  return useMemo(() => {
    const key = normalizeAssigneeKey(raw);
    return assigneeMatches.get(key) ?? null;
  }, [assigneeMatches, raw]);
}
